export const DIRECTORY_CSV_HEADERS = {
    PERSONAL_NAME: "Personal Name",
    COMPANY_NAME: "Company Name",
    POSITION: "Title/Position",
    DEPARTMENT: "Department",
    EMAIL: "Email",
    PHONE: "Phone",
    ADDRESS: "Address",
    LOCATION: "Location",
    WEBSITE: "Website",
    DESCRIPTION: "Description"
} as const;

export const DIRECTORY_CSV_REQUIRED_HEADERS: string[] = Object.values(DIRECTORY_CSV_HEADERS);

export type DirectoryVisibility = "employee" | "client" | "employee_and_client";

export interface DirectoryCustomFieldDefinition {
    uuid: string;
    name: string;
}

export interface DirectoryCsvRow {
    id: string;
    title: string;
    company: string;
    position: string;
    department: string;
    email: string;
    phoneNumber: string;
    address: string;
    country: string;
    website: string;
    description: string;
    visibility: DirectoryVisibility;
    customFields: Record<string, string>;
}

export interface DirectoryContactPayload {
    title: string;
    company: string;
    position: string;
    department: string;
    email: string;
    phoneNumber: string;
    address: string;
    country: string;
    website: string;
    description: string;
    imageUrl: null;
    expirationDate: null;
    visibleToEmployees: boolean;
    visibleToClients: boolean;
    customFields: Array<{
        companyFieldUuid: string;
        value: string;
    }>;
}

export interface CsvParseResults {
    data: Array<Record<string, unknown>>;
    meta?: {
        fields?: string[];
    };
}

export interface DirectoryCsvParseResult {
    rows: DirectoryCsvRow[];
    errors: string[];
}

export const validateDirectoryCsvRows = (rows: DirectoryCsvRow[]): string[] => {
    const errors: string[] = [];

    rows.forEach((row, index) => {
        if (!row.title.trim() && !row.company.trim()) {
            errors.push(`Row ${index + 1}: enter a Personal Name or Company Name.`);
        }

        if (row.email.trim() && !/^\S+@\S+\.\S+$/.test(row.email.trim())) {
            errors.push(`Row ${index + 1}: enter a valid Email.`);
        }
    });

    if (rows.length === 0) {
        errors.push("The CSV does not contain any listings.");
    }

    return errors;
};

const customFieldHeader = (name: string): string => `Custom Field: ${name}`;

const csvCell = (value: string): string => `"${value.replace(/"/g, "\"\"")}"`;

const normalizedValue = (value: unknown): string => {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
    }

    return "";
};

const hasContent = (record: Record<string, unknown>, headers: string[]): boolean => {
    return headers.some(header => normalizedValue(record[header]).length > 0);
};

export const getDirectoryCsvHeaders = (customFields: DirectoryCustomFieldDefinition[]): string[] => [
    ...DIRECTORY_CSV_REQUIRED_HEADERS,
    ...customFields.map(field => customFieldHeader(field.name))
];

export const createDirectoryCsvTemplate = (customFields: DirectoryCustomFieldDefinition[]): string => {
    const headers = getDirectoryCsvHeaders(customFields);
    const exampleValues = [
        "Alex Morgan",
        "Example Company",
        "Operations Manager",
        "Operations",
        "alex@example.com",
        "+61400000000",
        "123 Example Street",
        "Australia",
        "https://example.com",
        "Example directory listing",
        ...customFields.map(() => "")
    ];

    return `${headers.map(csvCell).join(",")}\r\n${exampleValues.map(csvCell).join(",")}\r\n`;
};

export const downloadDirectoryCsvTemplate = (customFields: DirectoryCustomFieldDefinition[]): void => {
    const blob = new Blob([createDirectoryCsvTemplate(customFields)], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "DirectoryTemplate.csv";
    link.click();
    window.URL.revokeObjectURL(url);
};

export const parseDirectoryCsv = (
    results: CsvParseResults,
    visibility: DirectoryVisibility,
    customFields: DirectoryCustomFieldDefinition[]
): DirectoryCsvParseResult => {
    const fields = results.meta?.fields || Object.keys(results.data[0] || {});
    const missingHeaders = DIRECTORY_CSV_REQUIRED_HEADERS.filter(header => !fields.includes(header));

    if (missingHeaders.length > 0) {
        return {
            rows: [],
            errors: [`The CSV is missing the following columns: ${missingHeaders.join(", ")}.`]
        };
    }

    const knownHeaders = getDirectoryCsvHeaders(customFields);
    const populatedRecords = results.data.filter(record => hasContent(record, knownHeaders));
    const rows = populatedRecords.map((record, index): DirectoryCsvRow => {
        const title = normalizedValue(record[DIRECTORY_CSV_HEADERS.PERSONAL_NAME]);
        const company = normalizedValue(record[DIRECTORY_CSV_HEADERS.COMPANY_NAME]);
        const email = normalizedValue(record[DIRECTORY_CSV_HEADERS.EMAIL]);

        return {
            id: `directory-csv-row-${index}`,
            title,
            company,
            position: normalizedValue(record[DIRECTORY_CSV_HEADERS.POSITION]),
            department: normalizedValue(record[DIRECTORY_CSV_HEADERS.DEPARTMENT]),
            email,
            phoneNumber: normalizedValue(record[DIRECTORY_CSV_HEADERS.PHONE]),
            address: normalizedValue(record[DIRECTORY_CSV_HEADERS.ADDRESS]),
            country: normalizedValue(record[DIRECTORY_CSV_HEADERS.LOCATION]),
            website: normalizedValue(record[DIRECTORY_CSV_HEADERS.WEBSITE]),
            description: normalizedValue(record[DIRECTORY_CSV_HEADERS.DESCRIPTION]),
            visibility,
            customFields: Object.fromEntries(customFields.map(field => [
                field.uuid,
                normalizedValue(record[customFieldHeader(field.name)])
            ]))
        };
    });

    return {
        rows,
        errors: validateDirectoryCsvRows(rows).map(error => error.replace(/^Row (\d+):/, (_, rowNumber) => (
            `Row ${Number(rowNumber) + 1}:`
        )))
    };
};

export const directoryCsvRowToPayload = (
    row: DirectoryCsvRow,
    customFields: DirectoryCustomFieldDefinition[]
): DirectoryContactPayload => ({
    title: row.title.trim(),
    company: row.company.trim(),
    position: row.position.trim(),
    department: row.department.trim(),
    email: row.email.trim(),
    phoneNumber: row.phoneNumber.trim(),
    address: row.address.trim(),
    country: row.country.trim(),
    website: row.website.trim(),
    description: row.description.trim(),
    imageUrl: null,
    expirationDate: null,
    visibleToEmployees: row.visibility === "employee" || row.visibility === "employee_and_client",
    visibleToClients: row.visibility === "client" || row.visibility === "employee_and_client",
    customFields: customFields.map(field => ({
        companyFieldUuid: field.uuid,
        value: row.customFields[field.uuid]?.trim() || ""
    }))
});
